import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import SbrDrawer from '../sbr/SbrDrawer';
import { addRouteStops, createRoutePlan, getRoutePlans, toRouteDate } from '../../services/routePlans';
import { getUsersAsOptions } from '../../services/companyUsers';
import { onMutateSuccess } from '../../utils/queryResponse';
import { ROUTE_ACTIVITIES, activityTime, canBeRouteAssignee, suggestActivity } from '../../utils/routeActivity';
import { getCivicAddress, toLocalDateString } from '../../utils/helper';
import { MISC_LABELS, roleListLabel } from '../../constants/labels';
import { useTranslation } from 'react-i18next';
import DateInput from '../forms/dateInput';

const RouteIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" /><circle cx="18" cy="5" r="3" /></svg>
);

const styles = `
  .sbr .atr-field{display:flex;flex-direction:column;gap:6px;margin-bottom:14px}
  .sbr .atr-field label{font-size:12px;font-weight:800;color:var(--text)}
  .sbr .atr-field input,.sbr .atr-field select{border:1px solid var(--line);border-radius:10px;padding:10px 12px;font:600 13.5px inherit;color:var(--ink);background:#fff;outline:0}
  .sbr .atr-field input:focus,.sbr .atr-field select:focus{border-color:var(--brand)}
  .sbr .atr-plans{display:flex;flex-direction:column;gap:8px}
  .sbr .atr-plan{display:flex;align-items:center;gap:10px;padding:10px 12px;border:1px solid var(--line);border-radius:10px;cursor:pointer;background:#fff}
  .sbr .atr-plan.on{border-color:var(--brand);background:var(--brand-50)}
  .sbr .atr-plan .nm{font-weight:800;font-size:13px;color:var(--ink)}
  .sbr .atr-plan .meta{font-size:12px;color:var(--muted);font-weight:600}
  .sbr .atr-jobs{display:flex;flex-direction:column;gap:8px;margin-top:6px}
  .sbr .atr-job{display:grid;grid-template-columns:1fr auto;gap:8px 12px;align-items:center;padding:10px 12px;border:1px solid var(--line);border-radius:10px;background:#fff}
  .sbr .atr-job .t{font-weight:800;font-size:13px;color:var(--ink)}
  .sbr .atr-job .s{font-size:12px;color:var(--muted);font-weight:600;margin-top:2px}
  .sbr .atr-job select{border:1px solid var(--line);border-radius:8px;padding:6px 8px;font:700 12px inherit;color:var(--ink);background:#fff}
  .sbr .atr-sec{font-size:11px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--faint);margin:16px 0 8px}
`;

/**
 * Put the selected jobs on a route plan for a day — either an existing plan
 * for that date or a new one (optionally assigned to a driver). Each job
 * gets an activity (pickup / delivery / on-site), suggested from its stage.
 */
const AddToRouteDrawer = ({ show, onHide, jobs = [], initialDate, initialPlanId, onDone }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const [date, setDate] = useState(initialDate || toRouteDate());
    const [target, setTarget] = useState(initialPlanId || 'new');          // plan id | 'new'
    const [name, setName] = useState('');
    const [driverId, setDriverId] = useState('');
    const [activities, setActivities] = useState({});

    useEffect(() => {
        if (!show) return;
        setDate(initialDate || toRouteDate());
        setTarget(initialPlanId || 'new');
        setName('');
        setDriverId('');
        const next = {};
        jobs.forEach(j => { next[j.id] = suggestActivity(j); });
        setActivities(next);
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const { data: plansData } = useQuery(['route-plans', date], () => getRoutePlans(date), { enabled: show && !!date });
    const plans = useMemo(() => (plansData?.code === 200 && Array.isArray(plansData.message) ? plansData.message : []), [plansData]);

    const { data: usersData } = useQuery(['users-option'], getUsersAsOptions, { enabled: show });
    const assignees = useMemo(() => {
        const all = usersData?.code === 200 && Array.isArray(usersData.message) ? usersData.message : [];
        // A route is worked by a driver or a technician — nobody else is offered.
        return all.filter(canBeRouteAssignee);
    }, [usersData]);

    // If the plan we were pointed at isn't on this date, fall back to "new".
    useEffect(() => {
        if (target !== 'new' && plansData && !plans.some(p => p.id === target)) setTarget('new');
    }, [plansData, plans, target]);

    const stops = jobs.map(j => ({ jobId: j.id, activity: activities[j.id] || suggestActivity(j) }));

    const finish = (plan) => {
        // Remove the cached list outright rather than marking it stale: the
        // drawer unmounts on the same tick, which can cancel an invalidate's
        // in-flight refetch and leave the page we are navigating to showing the
        // pre-creation cache.
        queryClient.removeQueries(['route-plans']);
        onHide();
        onDone?.(plan);
        navigate(`/route-plan?date=${date}&plan=${plan.id}`);
    };

    const { mutate: create, isLoading: creating } = useMutation(createRoutePlan, {
        onSuccess: onMutateSuccess((plan) => finish(plan), t('addToRoute.routePlanCreated')),
    });
    const { mutate: append, isLoading: appending } = useMutation(addRouteStops, {
        onSuccess: onMutateSuccess((plan) => finish(plan), t('addToRoute.stopsAdded')),
    });

    const submit = (e) => {
        e.preventDefault();
        if (!stops.length) return;
        if (target === 'new') create({ date, name: name || null, driverId: driverId || null, stops });
        else append({ id: target, stops });
    };

    const busy = creating || appending;

    return (
        <SbrDrawer
            show={show}
            onHide={onHide}
            secondary
            icon={<RouteIcon />}
            title={t('addToRoute.title')}
            subtitle={t('addToRoute.jobsSelected', { count: jobs.length })}
            footer={
                <>
                    <button type="button" className="btn-sbr btn-ghost-sbr" onClick={onHide} disabled={busy}>{t('common.cancel')}</button>
                    <button type="submit" form="add-to-route" className="btn-sbr btn-primary-sbr" disabled={busy || !stops.length}>
                        {busy ? t('common.saving') : t('addToRoute.addStops', { count: stops.length })}
                    </button>
                </>
            }
        >
            <style>{styles}</style>
            <form id="add-to-route" onSubmit={submit}>
                <div className="atr-field">
                    <label>{t('addToRoute.routeDate')}</label>
                    <DateInput value={date} onChange={(e) => { setDate(e.target.value); setTarget('new'); }} required />
                </div>

                <div className="atr-sec">{t('addToRoute.routePlan')}</div>
                <div className="atr-plans">
                    {plans.map(p => (
                        <div key={p.id} className={`atr-plan${target === p.id ? ' on' : ''}`} onClick={() => setTarget(p.id)}>
                            <input type="radio" name="plan" checked={target === p.id} onChange={() => setTarget(p.id)} />
                            <div>
                                <div className="nm">{p.name || (p.driver ? t('addToRoute.driversRoute', { name: p.driver.fullName }) : t('routePlan.route'))}</div>
                                <div className="meta">{t('routePlan.stop', { count: p.stops?.length || 0 })}{p.driver ? ` · ${p.driver.fullName}` : ` · ${t('addToRoute.notAssignedYet')}`}</div>
                            </div>
                        </div>
                    ))}
                    <div className={`atr-plan${target === 'new' ? ' on' : ''}`} onClick={() => setTarget('new')}>
                        <input type="radio" name="plan" checked={target === 'new'} onChange={() => setTarget('new')} />
                        <div>
                            <div className="nm">{t('addToRoute.newRoutePlan')}</div>
                            <div className="meta">{plans.length ? t('addToRoute.startAnother') : t('addToRoute.firstRoute')}</div>
                        </div>
                    </div>
                </div>

                {target === 'new' && (
                    <>
                        <div className="atr-field" style={{ marginTop: 14 }}>
                            <label>{t('addToRoute.nameOptional')}</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('addToRoute.namePlaceholder')} />
                        </div>
                        <div className="atr-field">
                            <label>{MISC_LABELS.assignedTo}</label>
                            <select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
                                <option value="">{t('addToRoute.assignLater')}</option>
                                {assignees.map(u => <option key={u.id} value={u.id}>{u.fullName}{u.roles ? ` · ${roleListLabel(u.roles)}` : ''}</option>)}
                            </select>
                            <small style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600 }}>{t('addToRoute.assignHint')}</small>
                        </div>
                    </>
                )}

                <div className="atr-sec">{t('addToRoute.stops')}</div>
                <div className="atr-jobs">
                    {jobs.map(j => {
                        const activity = activities[j.id] || suggestActivity(j);
                        const when = activityTime(j, activity);
                        return (
                            <div key={j.id} className="atr-job">
                                <div>
                                    <div className="t">#{j.id} · {j.client?.firstName} {j.client?.lastName}</div>
                                    <div className="s">{getCivicAddress(j.client) || '—'}{when ? ` · ${toLocalDateString(when, 'dd.MM HH:mm')}` : ''}</div>
                                </div>
                                <select value={activity} onChange={(e) => setActivities(a => ({ ...a, [j.id]: e.target.value }))} aria-label={t('addToRoute.activityForJob', { id: j.id })}>
                                    {ROUTE_ACTIVITIES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                            </div>
                        );
                    })}
                </div>
            </form>
        </SbrDrawer>
    );
};

export default AddToRouteDrawer;
